'use strict';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CDN } from './config';
/**
 * Runtime loaders for the 3D-avatar ESM dependencies (TalkingHead, HeadAudio)
 * and LiveKit (HeyGen).
 *
 * These are ES modules served from a CDN / the asset host. They are loaded by
 * injecting a `<script type="module">` whose body is a STRING — so the native
 * `import()` lives in markup, never in this bundle (the production build
 * minifies chunks with uglify-js, which cannot parse a native dynamic import).
 *
 * The injected module runs in its own scope, so it hands the loaded value back
 * via a one-shot `CustomEvent` (carrying the reference in `detail`). The
 * readiness promises are kept as module-level state here — nothing is attached
 * to `window`.
 */
let talkingHeadPromise = null;
let headAudioPromise = null;
let liveKitPromise = null;
let eventSeq = 0;
/**
 * Load an ES module via an injected module script and resolve with the picked
 * export (or the whole namespace). When `blob` is set the script first fetches
 * the source and re-serves it through a `Blob` with a correct `text/javascript`
 * MIME type — so a host serving `.mjs` with a wrong/empty `Content-Type` still
 * loads (only safe for self-contained modules without relative imports).
 */
function loadEsmViaScript(url, exportName, blob = false) {
    return new Promise((resolve, reject) => {
        const eventName = `zoovu:avatar:module:${eventSeq++}`;
        const onReady = (e) => {
            window.removeEventListener(eventName, onReady);
            const detail = e.detail;
            if (detail && detail.ok) {
                resolve(detail.value);
            }
            else {
                reject((detail && detail.error) || new Error(`Failed to load module: ${url}`));
            }
        };
        window.addEventListener(eventName, onReady);
        const pick = exportName ? `(m[${JSON.stringify(exportName)}] || m.default)` : 'm';
        const resolveSrc = blob
            ? `const r = await fetch(${JSON.stringify(url)}); if (!r.ok) throw new Error('HTTP ' + r.status); const src = URL.createObjectURL(new Blob([await r.text()], { type: 'text/javascript' }));`
            : `const src = ${JSON.stringify(url)};`;
        const revoke = blob ? 'URL.revokeObjectURL(src);' : '';
        const loader = document.createElement('script');
        loader.type = 'module';
        loader.textContent = `
            (async () => {
                try {
                    ${resolveSrc}
                    const m = await import(src);
                    ${revoke}
                    window.dispatchEvent(new CustomEvent(${JSON.stringify(eventName)}, { detail: { ok: true, value: ${pick} } }));
                } catch (error) {
                    window.dispatchEvent(new CustomEvent(${JSON.stringify(eventName)}, { detail: { ok: false, error: error } }));
                }
            })();
        `;
        document.head.appendChild(loader);
    });
}
/** id of the import map we inject (lets us find/dedupe our own element). */
const AVATAR_IMPORT_MAP_ID = 'zoovu-avatar-importmap';
/** Build & append our import map (maps the bare `three`/`talkinghead` specifiers). */
function injectAvatarImportMap() {
    if (document.getElementById(AVATAR_IMPORT_MAP_ID))
        return;
    const importMap = document.createElement('script');
    importMap.type = 'importmap';
    importMap.id = AVATAR_IMPORT_MAP_ID;
    importMap.textContent = JSON.stringify({
        imports: {
            three: CDN.three,
            'three/addons/': CDN.threeAddons,
            talkinghead: CDN.talkingHead
        }
    });
    document.head.appendChild(importMap);
}
/** Does the given `<script type="importmap">` already resolve the bare `three` specifier? */
function importMapProvidesThree(mapEl) {
    try {
        const parsed = JSON.parse(mapEl.textContent || '{}');
        const imports = parsed && parsed.imports;
        return !!(imports && typeof imports.three === 'string' && imports.three.length > 0);
    }
    catch (e) {
        // Unparseable map — can't confirm it provides `three`; assume it doesn't.
        return false;
    }
}
/**
 * Ensure TalkingHead (and its `three` dependency) is loaded; resolves with the
 * TalkingHead class. Idempotent.
 */
export function ensureTalkingHead() {
    if (talkingHeadPromise) {
        return talkingHeadPromise;
    }
    // Register an import map that maps the bare `three`/`talkinghead` specifiers
    // (used inside talkinghead.mjs) to CDN ESM builds.
    //
    // Import-map semantics differ by browser: older engines honour only the FIRST
    // map and ignore later ones; Chrome 133+ (and other modern engines) MERGE
    // multiple maps, first-wins on key conflicts. We handle every case:
    //  - No map on the page → inject ours.
    //  - A map already resolves `three` → leave it; ours would be redundant (and
    //    on merge-capable browsers would be ignored for `three` anyway).
    //  - A map exists but lacks `three` → still inject ours (best-effort): it
    //    fixes the load on merge-capable browsers and is harmlessly ignored on
    //    older ones. We also warn, since on non-merging browsers the only real
    //    fix is the integrator adding `three` to their own map.
    const existingMap = document.querySelector('script[type="importmap"]');
    if (!existingMap) {
        injectAvatarImportMap();
    }
    else if (!importMapProvidesThree(existingMap)) {
        injectAvatarImportMap();
        console.warn('[avatar] An import map already exists on the page but does not map "three". '
            + 'On browsers without multiple-import-map support the 3D avatar (TalkingHead) '
            + `may fail to load — add a "three" entry pointing to a three.js ESM build (e.g. "${CDN.three}") `
            + 'to your import map.');
    }
    talkingHeadPromise = loadEsmViaScript(CDN.talkingHead, 'TalkingHead');
    return talkingHeadPromise;
}
/**
 * Fetch a script and return a same-origin **blob URL** carrying a correct
 * `text/javascript` MIME type, so `AudioWorklet.addModule()` succeeds even when
 * the asset host serves `.mjs`/`.js` with a wrong/empty `Content-Type`. Falls
 * back to the original URL if the fetch fails (e.g. CORS).
 */
export function toScriptBlobUrl(url) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const res = yield fetch(url);
            if (!res.ok) {
                throw new Error('HTTP ' + res.status);
            }
            const code = yield res.text();
            return URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
        }
        catch (e) {
            console.warn('[avatar] Could not blob-wrap', url, '— using direct URL.', e);
            return url;
        }
    });
}
/**
 * Ensure the HeadAudio lip-sync module is loaded; resolves with the HeadAudio
 * class. Blob-wrapped (the asset host may serve `.mjs` with a wrong MIME type).
 * Idempotent.
 *
 * @param headAudioUrl absolute URL to `headaudio.min.mjs`
 */
export function ensureHeadAudio(headAudioUrl) {
    if (!headAudioPromise) {
        headAudioPromise = loadEsmViaScript(headAudioUrl, 'HeadAudio', true);
    }
    return headAudioPromise;
}
/**
 * Ensure the LiveKit client (HeyGen renderer) is loaded; resolves with the
 * module namespace (`{ Room, RoomEvent, Track, … }`). Idempotent.
 */
export function ensureLiveKit() {
    if (!liveKitPromise) {
        liveKitPromise = loadEsmViaScript(CDN.liveKit);
    }
    return liveKitPromise;
}
